import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { useContext, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import { EnergyRating } from "../../../components/EnergyRating";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import { queryImportantIndetification, updateImportantIndetification } from "../../../utility/service/carbon-service";
import {
  queryEvaluationByPlanSn,
  queryEvaluationTemplate,
  queryEvaluationTemplateDetail,
  updateImportanceEvaluation,
} from "../../../utility/service/importanceIdentification-service";
import { checkDpSuccess } from "../../../utility/service/tool-service";

const evaluateOpTemplate = (option) => {
  if (option) {
    return (
      <div className="flex align-items-center">
        <div>
          {option?.evaluateIdName} ( {option?.evaluationValue} )
        </div>
      </div>
    );
  }
};

const ImportanceIdentification = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { itemObj, setItemObj, setAction, action } = useOutletContext();
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const [editable, setEditable] = useState(false);
  const [rows, setRows] = useState([]);
  const [templateListOpt, setTemplateListOpt] = useState([]);
  const [evaluationDetailsList, setEvaluationDetailsList] = useState([]);
  const [evaluateSettingList, setEvaluateSettingList] = useState([]);
  const [evalSettingKey, setEvalSettingKey] = useState([]);
  const [currentEvalTemp, setCurrentEvalTemp] = useState({});
  const [isExistData, setIsExistData] = useState(false);
  const [dropdownEnabled, setDropdownEnabled] = useState(false);
  const isEditable = useEditableFlag("F10103"); // control edit by F10103
  const [threshold, setThreshold] = useState(0);
  const [, loading] = useContext(SystemLoading);

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    control,
    clearErrors,
    reset,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  const {
    fields: evalSettingFields,
    append: addSettingForm,
    remove: delSettingForm,
    insert: insertSettingForm,
  } = useFieldArray({
    control,
    name: "evaluationDetailsList",
  });

  useEffect(() => {
    if (itemObj?.planSn) {
      axios_queryEvaluationByPlanSn(itemObj?.planSn);
      queryEvaluationTemplate({
        companySeq: itemObj?.companySeq,
        useCase: "U",
      }).then((res) => {
        if (checkDpSuccess(res)) {
          let data = res?.data?.RespBody?.evaluationTemplates?.options;
          setTemplateListOpt(data);
        }
      });
    }
  }, []);

  const axios_queryEvaluationByPlanSn = (planSn) => {
    checkLoading(loading);
    queryEvaluationByPlanSn({ planSn })
      .then((res) => {
        if (checkDpSuccess(res)) {
          setIsExistData(true);
          setEditable(false);
          setDropdownEnabled(false);
          setValue("evaluationTemplateId", "");
          setEvaluationDetailsList(res?.data?.RespBody?.evaluationDetailsList);
          setValue("evaluationDetailsList", res?.data?.RespBody?.evaluationDetailsList);

          setEvaluateSettingList(res?.data?.RespBody?.evaluateSettingList);
          let settingKeyList = res?.data?.RespBody?.evaluateSettingList;
          const settingKeyArray = settingKeyList?.map((item) => item.evaluationId);
          setEvalSettingKey(settingKeyArray);

          res?.data?.RespBody?.evaluationDetailsList?.forEach((item, index) => {
            item?.selectEvaluateList?.forEach((subItem) => {
              const key = subItem.evaluationIdKey;
              const value = subItem.evaluationIdValue;

              // 如果 key 是你設定的清單裡的，就處理
              if (settingKeyArray.includes(key)) {
                setValue(`evaluationDetailsList.${index}.${key}.evaluationValue`, value);
              }
            });
          });

          let resultData = res?.data?.RespBody;
          let evalsettingSortList = resultData?.evaluateSettingList?.map((item, index) => {
            // 先處理 evaluateSettingDList 的 sort
            const updatedDList =
              item?.evaluateSettingDList?.map((subItem, subIndex) => ({
                ...subItem,
                sort: (subIndex + 1).toString(),
              })) || [];

            // 回傳主結構 + 更新 sort
            return {
              ...item,
              sort: (index + 1).toString(),
              evaluateSettingDList: updatedDList,
            };
          });

          let currentEvalTemp = {
            evaluationTemplateId: resultData?.evaluationTemplateId,
            enableFlag: resultData?.enableFlag,
            evaluationMethod: resultData?.evaluationMethod,
            evaluationTemplateName: resultData?.evaluationTemplateName,
            score: resultData?.score,
            evaluateSettingList: evalsettingSortList,
          };
          setCurrentEvalTemp(currentEvalTemp);
          setThreshold(Number(resultData?.score));
        } else {
          // 清空資料 如果查無資料
          setCurrentEvalTemp({});
          setEvaluationDetailsList([]);
          setEvaluateSettingList([]);
          setEvalSettingKey([]);
          setValue("evaluationDetailsList", []);
          setValue("evaluationTemplateId", "");
          setIsExistData(false);
          setDropdownEnabled(true);
          setThreshold(0);
        }
      })
      .finally(() => checkLoading(loading, false));
  };

  const axios_queryEvaluationTemplateDetail = (tempId) => {
    queryEvaluationTemplateDetail({ evaluationTemplateId: tempId }).then((res) => {
      setEvaluationDetailsList(res?.data?.RespBody?.evaluationDetailsList);
      setValue("evaluationDetailsList", res?.data?.RespBody?.evaluationDetailsList);
      setEvaluateSettingList(res?.data?.RespBody?.evaluateSettingList);
      let settingKeyList = res?.data?.RespBody?.evaluateSettingList;
      const settingKeyArray = settingKeyList?.map((item) => item.evaluationId);
      setEvalSettingKey(settingKeyArray);
      let resultData = res?.data?.RespBody;
      let evalsettingSortList = resultData?.evaluateSettingList?.map((item, index) => {
        // 先處理 evaluateSettingDList 的 sort
        const updatedDList =
          item?.evaluateSettingDList?.map((subItem, subIndex) => ({
            ...subItem,
            sort: (subIndex + 1).toString(),
          })) || [];

        // 回傳主結構 + 更新 sort
        return {
          ...item,
          sort: (index + 1).toString(),
          evaluateSettingDList: updatedDList,
        };
      });

      let currentEvalTemp = {
        evaluationTemplateId: resultData?.evaluationTemplateId,
        enableFlag: resultData?.enableFlag,
        evaluationMethod: resultData?.evaluationMethod,
        evaluationTemplateName: resultData?.evaluationTemplateName,
        score: resultData?.score,
        evaluateSettingList: evalsettingSortList,
      };
      setCurrentEvalTemp(currentEvalTemp);
      setThreshold(Number(resultData?.score));
    });
  };

  const EvaluationCard = ({ detail, index, evaluateSettingList, control, editable, getValues, threshold, t }) => {
    // 建立 evaluationId -> evaluationValue 的映射
    const evaluationMap = evaluateSettingList?.reduce((acc, item) => {
      item.evaluateSettingDList?.forEach((subItem) => {
        if (subItem.evaluationId !== undefined && subItem.evaluationValue !== undefined) {
          acc[subItem.evaluationId] = subItem.evaluationValue;
        }
      });
      return acc;
    }, {});

    // 取得所有 field name
    const fieldNames = evaluateSettingList.map(
      (item) => `evaluationDetailsList.${index}.${item.evaluationId}.evaluationValue`,
    );

    // 監控所有 field 的值
    const watchedValues = useWatch({
      control,
      name: fieldNames,
    });

    const safeWatchedValues = Array.isArray(watchedValues) ? watchedValues : [];

    // 計算總分
    const totalSum = safeWatchedValues.reduce((accumulator, fieldValue) => {
      const valueToAdd = Number(evaluationMap[fieldValue]) || 0;
      return accumulator + valueToAdd;
    }, 0);

    const isImportant = totalSum > threshold;

    return (
      <div
        className={`importance-identification-card ${isImportant ? "Y" : "N"}`}
        key={detail?.evaluationResultId || detail?.evaluationDetailsSeq}
      >
        <header>
          {isImportant && <span className="y-tag">{t("evaluationResult_important")}</span>}
          <div className="grid">
            <div className="col-12 md:col-6">
              {t("evaluation_items")}: {detail?.displayName}
            </div>
            <div className="col-12 md:col-6 lg:text-right md:text-right">
              {t("totalScore")}: {totalSum}
            </div>
          </div>
        </header>
        <ul>
          {evaluateSettingList?.map((item) => (
            <li key={`evaluationDetailsList.${index}.${item.evaluationId}.evaluationValue`}>
              <span>{item?.evaluateIdName}</span>
              <Controller
                name={`evaluationDetailsList.${index}.${item.evaluationId}.evaluationValue`}
                control={control}
                render={({ field, fieldState }) => (
                  <Dropdown
                    disabled={!editable && !getValues("evaluationTemplateId")}
                    showClear={field.value}
                    itemTemplate={evaluateOpTemplate}
                    optionLabel="evaluateIdName"
                    optionValue="evaluationId"
                    value={field.value || ""}
                    placeholder={t("plz_chs")}
                    options={item?.evaluateSettingDList}
                    className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                    onChange={(event) => field.onChange(event.value ?? null)}
                  />
                )}
              />
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const ListTemplate = ({ items }) => {
    if (!items || items.length === 0) return null;

    let list = items?.map((detail, index) => {
      return (
        <EvaluationCard
          key={index}
          detail={detail}
          index={index}
          evaluateSettingList={evaluateSettingList}
          control={control}
          editable={editable}
          getValues={getValues}
          threshold={threshold}
          t={t}
        />
      );
    });

    return <div className="importance-identification-card-wrapper">{list}</div>;
  };

  const onSubmit = (data) => {
    const newEvaluationDetailsList = data?.evaluationDetailsList?.map((item, index) => {
      // 1. 過濾掉 evalSettingKey 的 key-value
      const cleanedItem = Object.fromEntries(
        Object.entries(item).filter(([key]) => !evalSettingKey.includes(key) && key !== "evaluationResultId"),
      );
      // 2. 建立 selectEvaluateList
      const selectEvaluateList = evalSettingKey.reduce((acc, key) => {
        if (item[key]?.evaluationValue) {
          acc.push({
            evaluationIdKey: key,
            evaluationIdValue: item[key].evaluationValue,
          });
        }
        return acc;
      }, []);
      // 3. 回傳處理後的新物件
      return {
        //   ...item,
        ...cleanedItem,
        sort: (index + 1).toString(),
        selectEvaluateList,
      };
    });

    let reqBody = {
      planSn: itemObj?.planSn,
      evaluationTemplateId: data.evaluationTemplateId || currentEvalTemp?.evaluationTemplateId,
      evaluationTemplateName: currentEvalTemp?.evaluationTemplateName,
      evaluationMethod: currentEvalTemp?.evaluationMethod,
      enableFlag: currentEvalTemp?.enableFlag,
      score: currentEvalTemp?.score,
      evaluateSettingList: currentEvalTemp?.evaluateSettingList,
      evaluationDetailsList: newEvaluationDetailsList,
    };

    updateImportanceEvaluation(reqBody).then((res) => {
      if (checkDpSuccess(res)) {
        handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
        axios_queryEvaluationByPlanSn(itemObj?.planSn); //點取消後重新取得09資料
      }
    });

    // "selectEvaluateList" : [ {
    //     "evaluationIdKey" : "1",
    //     "evaluationIdValue" : "1"
    //   } ]
    // const respBody =  rows?.map((row)=>{
    //     return {
    //         ...row,
    //         activityDataSize:data[`activityDataSize_${row?.typeSeq}`],
    //         activityDataAccessibility:data[`activityDataAccessibility_${row?.typeSeq}`],
    //         emissionsDataAccessibility:data[`emissionsDataAccessibility_${row?.typeSeq}`],
    //         improvementParticipation:data[`improvementParticipation_${row?.typeSeq}`],
    //         outsourcedOperations:data[`outsourcedOperations_${row?.typeSeq}`],
    //         externalRequirements:data[`externalRequirements_${row?.typeSeq}`]
    //     }
    // })
    // // console.table(respBody);
    // axios_updateImportantIndetification({planSn:itemObj?.planSn,importanceList:respBody})
  };

  const transToInt = (num) => {
    if (num) return parseInt(num);
    else return 0;
  };
  const setInitialValues = (rows) => {
    rows?.map((row, index) => {
      Object.keys(row).forEach((key) => {
        if (
          key !== "typeSeq" &&
          key !== "typeName" &&
          key !== "evaluationResult" &&
          key !== "classNo" &&
          key !== "score"
        ) {
          // 這裡的 key 會是 activityDataSize、activityDataAccessibility 等等
          setValue(`${key}_${row.typeSeq}`, row[key]);
        }
      });
    });
  };
  // 舊 固定欄位的API 查詢
  const axios_queryImportantIndetification = (planSn) => {
    queryImportantIndetification({ planSn }).then((res) => {
      if (checkDpSuccess(res)) {
        let result = res?.data?.RespBody?.importanceList;
        setRows(result);
        setInitialValues(result);
      }
    });
  };
  // 舊 固定欄位的API 更新
  const axios_updateImportantIndetification = (reqBody) => {
    updateImportantIndetification(reqBody).then((res) => {
      if (checkDpSuccess(res)) {
        let result = res?.data?.RespBody?.importanceList;
        setRows(result);
        setInitialValues(result);
        setEditable(false);
        handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
      }
    });
  };

  // 舊評分元件
  const energyRatingBody = (rowData, props) => {
    if (rowData) {
      return (
        <EnergyRating
          disabled={!editable}
          stars={3}
          name={`${props.field}_${rowData?.typeSeq}`}
          control={control}
          tooltip={`${rowData.typeName} [${props.column.props.header}]`}
        />
      );
    }
  };

  return (
    <form className="grid" onSubmit={handleSubmit(onSubmit)}>
      {/* <div className="flex gap-2 align-items-center pl-2">
        <i className="text-orange-500 pi pi-info-circle"></i>
        <span className="text-orange-500">{t('scoreStandard')}</span>
        </div> */}
      <div className="col-12 flex flex-wrap gap-2 align-items-end">
        <div className="w-6">
          <label htmlFor="countyCode" className="form-label">
            {t("evaluation_template")}
          </label>
          <Controller
            name="evaluationTemplateId"
            control={control}
            render={({ field, fieldState }) => (
              <Dropdown
                disabled={!dropdownEnabled}
                appendTo={"self"}
                optionLabel="label"
                optionValue="value"
                value={field.value}
                virtualScrollerOptions={{ itemSize: 40 }}
                defaultValue={""}
                placeholder={t("plz_chs")}
                name="evaluationTemplateId"
                control={control}
                options={templateListOpt}
                className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                onChange={(event) => {
                  field.onChange(event.value);
                  axios_queryEvaluationTemplateDetail(event.value);
                }}
              />
            )}
          />
        </div>
        <Button
          type="button"
          size="small"
          onClick={() => {
            navigate("evaluationSetting");
          }}
          label={t("evaluation_template_settings")}
          icon="pi pi-cog"
          disabled={!isEditable}
        />
      </div>

      {JSON.stringify(currentEvalTemp) !== "{}" && (
        <>
          <div className="col-12 md:col-6">
            <label className="label-has-icon">{t("evaluationTemplateName")}:</label>
            <span>{currentEvalTemp?.evaluationTemplateName}</span>
          </div>
          <div className="col-12 md:col-6">
            <label className="label-has-icon">{`${t("thresholdScore")}(>)`}:</label>
            <span>{currentEvalTemp?.score}</span>
          </div>
        </>
      )}

      {/* 使用PrimeReact DataView套件 會有render問題 */}
      {/* <DataView value={evaluationDetailsList} listTemplate={listTemplate} /> */}

      <div className="col-12">{<ListTemplate items={evalSettingFields} />}</div>

      {/* 之前的固定欄位寫法 */}
      {/* <DataTable
        stripedRows
        className="white-data-table hover-highlight no-shadow w-full"
        tableStyle={{ minWidth: '55rem' }}
        value={rows}
        emptyMessage={t('no_rec')}>

        <Column style={{minWidth:'200px'}} field="typeName" header={t('emissionCategory')} />
        <Column field="activityDataSize" header={t('activityDataSize')} body={energyRatingBody} />
        <Column field="activityDataAccessibility" header={t('dataAccessibility')} body={energyRatingBody} />
        <Column field="emissionsDataAccessibility" header={t('emissionFactorAccessibility')} body={energyRatingBody} />
        <Column field="improvementParticipation" header={t('oppoImprovePartic')} body={energyRatingBody} />
        <Column field="outsourcedOperations" header={t('outOperationService')} body={energyRatingBody} />
        <Column field="externalRequirements" header={t('externalRequire')} body={energyRatingBody} />
        <Column style={{minWidth:'80px'}} field="evaluationResult" header={t('result')} />

    </DataTable> */}

      {!editable && isExistData ? (
        <div className="col-12 gap-2 flex-all-center">
          <Button
            onClick={() => {
              setEditable(true);
              setDropdownEnabled(true);
            }}
            type="button"
            disabled={!isValid || !isEditable}
            label={t("button.edit")}
            size="small"
          />
          {/* <Button type="submit" label={t("button.confirm")} size="small" /> */}
        </div>
      ) : (
        <>
          {(getValues("evaluationTemplateId") !== "" || editable) && (
            <div className="col-12 gap-2 flex-all-center">
              <Button disabled={!isValid || !isEditable} type="submit" label={t("button.update")} size="small" />
              <Button
                type="button"
                outlined
                size="small"
                label={t("button.cancel")}
                onClick={(e) => {
                  e.preventDefault();
                  clearErrors();
                  setEditable(false);
                  axios_queryEvaluationByPlanSn(itemObj?.planSn); //點取消後重新取得09資料
                  setValue("evaluationTemplateId", "");
                }}
              />
            </div>
          )}
        </>
      )}
    </form>
  );
};

export default ImportanceIdentification;
